<template>
  <div class="container-fluid h-100 chat-container">
    <div class="row justify-content-center h-100 chat-row">
      <div class="col-md-12 col-xl-12 chat">
        <div class="card">
          <div class="card-header msg_head">
            <div class="d-flex bd-highlight">
              <div class="img_cont">
                <img
                  src="@/assets/articulearn-logo-a.png"
                  class="rounded-circle user_img"
                />
              </div>
              <div class="user_info">
                <span>{{ $t('chatScreen.header.title') }}</span>
              </div>
            </div>
            <!-- Ambient progress bar for freestyle chats and lessons -->
            <div
              v-if="
                dialogMeta?.type === 'FREESTYLE_CHAT' ||
                dialogMeta?.type === 'LESSON'
              "
              class="progress-bar-container"
              role="progressbar"
              :aria-label="progressBarLabel"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="Math.round(progressPercentage)"
            >
              <div
                class="progress-bar-fill"
                :class="[
                  progressBarClass,
                  {
                    complete: chatComplete || progressPercentage === 100,
                  },
                ]"
                :style="{
                  width: progressPercentage + '%',
                }"
              >
                <span
                  v-for="sparkle in sparkles"
                  :key="sparkle.id"
                  class="sparkle"
                  :class="`sparkle--${sparkle.size}`"
                  :style="{
                    left: sparkle.position + '%',
                    animationDelay: sparkle.delay + 's',
                  }"
                ></span>
              </div>
            </div>
          </div>
          <div class="card-body msg_card_body" ref="messageContainer">
            <div class="messages-wrapper">
              <ChatMessage
                v-for="(msg, index) in messages"
                :key="msg.id"
                :messageKey="msg.messageKey"
                :dialogId="dialogId"
                :speaker="msg.speaker"
                :msg="msg.text"
                :spokenText="msg.spokenText"
                :audioId="msg.audioId"
                :seenAt="msg.seenAt"
                :autoPlay="msg.autoPlay"
                :deferAudioFetch="msg.deferAudioFetch"
                :segmentPhase="segmentPhaseFor(msg)"
                :replay="msg.replay"
                :isMessageLoading="msg.isMessageLoading"
                :phase="msg.phase"
                :showIdentity="showTutorIdentity(index)"
                :interaction="msg.interaction"
                :opener="msg.opener"
                :interactionLive="isInteractionLive(msg, index)"
                @submit-interaction="handleInteractionSubmit(msg, $event)"
              />
            </div>
          </div>
          <div class="card-footer">
            <div class="input-group justify-content-center">
              <div class="view-stats" v-if="chatComplete">
                <DialogCompletion
                  :userId="userId"
                  :dialogId="dialogId"
                  :metadata="{
                    lang: dialogMeta?.lang,
                    courseId: dialogMeta?.courseId,
                    dialogType: dialogMeta?.type,
                    assessment: metadata?.assessment,
                  }"
                  @newChatRequest="emitNewChatRequest"
                />
              </div>
              <div v-else class="recorder-slot">
                <AudioRecorder
                  @new-recording="newRecordedAudio"
                  :disableButton="disableRecordButton"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AudioRecorder from "./AudioRecorder.vue";
import ChatMessage from "./ChatMessage.vue";
import { nextTick } from "vue";
import { useToast } from "@/composables/useToast.js";
import { useHaptics } from "@/composables/useHaptics.js";
import {
  getDatabase,
  ref,
  onChildAdded,
  onChildChanged,
  onValue,
  query,
  get,
  orderByChild,
  runTransaction,
} from "firebase/database";
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { authStore } from "@/store/auth";
import { audioConfig } from "@/store/audioConfig";
import { pendingTutorResponses, replyLagAnchors } from "@/store/pendingTutorResponses.js";
import {
  readRefusal,
  refusalMessageKey,
} from "@/scripts/continueChatErrors.js";
import {
  segmentPlaybackState,
  offerSegments,
  completeSegmentSession,
  endAllSegmentSessions,
} from "@/scripts/audioPlaybackService";
import DialogCompletion from "./dialog-completion/DialogCompletion.vue";
import { stopSlicesFor } from "@/scripts/slicePlayback.js";
import { clearClipCache } from "@/scripts/clipCache.js";

// How long a loading bubble may spin before we assume the reply is never
// coming and hand the mic back to the student (bug-hunt L8). Comfortably
// above the slowest observed tutor round trip.
const REPLY_WATCHDOG_MS = 120_000;

// Both continueChat call sites below already recover from a failed send in one
// catch block, and that recovery is the same whatever went wrong: release the
// dialog, pop the loading bubble, hand the mic back, toast. Only the toast's
// wording differs now, so the refusal code travels to the catch on the error
// rather than forking the send into two recovery paths. `refusalKey` is null
// for every failure that has no specific copy, which is every failure that
// existed before the turn bounds, and the catch then keeps its original toast.
function refusalError(status, code) {
  const error = new Error(`HTTP ${status}`);
  error.refusalKey = refusalMessageKey(code);
  error.status = status;
  return error;
}

// Whether a failed continueChat request could still produce a tutor reply.
// Every 4xx the endpoint answers (the 429 and 409 turn refusals, the 403, 400
// and 413) is returned before the agent runs, so no reply can follow one. A
// network error or a 5xx proves nothing either way: the backend may have
// finished the turn and written its reply after the client gave up. The
// reply-lag store uses this to avoid measuring such a late reply against the
// learner's next tap.
function replyMayStillArrive(error) {
  const status = error && error.status;
  return !(typeof status === "number" && status >= 400 && status < 500);
}

// How long after the "start chat" tap renderExistingMessagesAndGetIds still
// treats this mount as the one that tapped. Seconds, because the timestamp it
// compares against is whole seconds. A minute is wide enough to cover a cold
// function plus a slow device, and short enough that a learner coming back to
// an abandoned chat later is outside it.
const FRESH_OPENER_WINDOW_SECONDS = 60;

// How long a segment's audio may be in flight before the session gives up on
// it. Browser fetch has no default timeout, and a request that never settles
// leaves the session parked on the await: no clip ever starts, so no natural
// end can ever arrive, and the message sits showing that Arti is speaking with
// nothing audible. Failing instead suspends the session, which is what puts the
// stitched player back in front of the learner.
const SEGMENT_FETCH_TIMEOUT_MS = 10_000;

// One id per recording sent, so the backend can tell a re-issued copy of a
// submission apart from a genuinely new one.
//
// This exists because a PROD turn reached continueChat twice, 61.9 seconds
// apart, from the same phone, with multipart bodies differing by one byte, and
// nothing in this file re-sent it: sendMessage is a single fetch with no retry,
// no AbortController and no timeout, and the recorder's send button holds a
// synchronous latch. Some layer below the application code (the Android WebView
// network stack is the leading candidate) re-issued the POST. Whatever that
// layer is, it copies the body it was given verbatim, so an id minted here once
// per send arrives identical on both copies, which is exactly what lets the
// server suppress the second one.
//
// crypto.randomUUID is unavailable in older WebViews and on insecure origins,
// so the fallback builds the same shape from getRandomValues, and the last
// resort uses Math.random. The value only has to be unique per send from one
// device; it is never a secret and never a security boundary. It stays inside
// [A-Za-z0-9-] and well under the 64 characters the server accepts.
function newClientMessageId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (error) {
    // A locked-down WebView can throw on crypto access. An id is a nicety, not
    // a requirement: the server falls back to sentAt, which every installed
    // version already sends.
    console.warn("clientMessageId: crypto unavailable", error);
  }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 14);
}

// The backend writes a segmented reply's pieces under the message's `segments`
// child, index-addressed from 0. The Firebase JS SDK hands back an ARRAY when
// the keys are a dense run starting at 0 and a numeric-keyed OBJECT otherwise
// (a remainder that lands before the fast segment, a gap), so both shapes have
// to be read. Each entry is the piece's audio id; an object entry carrying its
// id under `audioId` is accepted too, so a future richer row shape cannot make
// the client silently stop chaining.
function orderedSegmentAudioIds(segments) {
  if (!segments || typeof segments !== "object") return [];
  const ordered = [];
  const entries = Array.isArray(segments)
    ? segments.map((value, index) => [index, value])
    : Object.keys(segments).map((key) => [Number(key), segments[key]]);
  for (const [index, value] of entries) {
    if (!Number.isInteger(index) || index < 0) continue;
    const audioId = typeof value === "string" ? value : value && value.audioId;
    if (audioId) ordered[index] = audioId;
  }
  return ordered;
}

// The replay object for a tutor message, or null for the learner's own.
// playbackControls switches on the speed chip, and it is true only in a lesson
// or a freestyle chat: the placement assessment measures the learner against
// the assessor's pace, and the background interview has nothing in the target
// language worth slowing. The learning language is the dialog's base code ("es"
// for "es-mx"), which the phrase-replay controls compare written spans against.
// A plain function rather than a method, so every harness that drives
// appendChatLog off the options object keeps working.
function buildReplay(messageData, dialogMeta) {
  if (messageData.speakerId !== "tutor") return null;
  const dialogType = dialogMeta?.type || null;
  const lang = dialogMeta?.lang;
  const replay = {
    playbackControls: dialogType === "LESSON" || dialogType === "FREESTYLE_CHAT",
    dialogType,
    learningLang:
      typeof lang === "string" && lang ? lang.split(/[-_]/)[0].toLowerCase() : null,
  };
  patchReplayFields(replay, messageData, { removeAbsent: false });
  return replay;
}

// The server-written phrase replay fields of a tutor message: the written
// spans of the learning-language phrases, the span timings of its audio and
// their version, and the silent tail's audio and timings (stage two). They
// travel inside the replay object so a change to them re-renders only this
// message.
const REPLAY_FIELDS = Object.freeze([
  "writtenSpans",
  "spanTimings",
  "timingsVersion",
  "tailAudioId",
  "tailTimings",
]);

// Copies each of those fields that `source` carries into `replay`, in place.
// With removeAbsent, a field the source does not carry is removed: a change
// event holds the whole message, so a field missing from it has been deleted
// on the server (RTDB stores no nulls), and keeping the old value would let a
// phrase tap cut the wrong span of the audio.
function patchReplayFields(replay, source, { removeAbsent }) {
  if (!replay || !source) return;
  REPLAY_FIELDS.forEach((field) => {
    const value = source[field];
    if (value !== undefined && value !== null) {
      replay[field] = value;
    } else if (removeAbsent && field in replay) {
      delete replay[field];
    }
  });
}

export default {
  name: "ChatScreen",
  components: {
    ChatMessage,
    AudioRecorder,
    DialogCompletion,
  },
  props: {
    dialogId: String,
    metadata: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      messages: [],
      numMessages: 0,
      userId: null,
      disableRecordButton: true,
      chatComplete: false,
      dialogMeta: null,
      mostRecentMessageIsFromTutor: false,
      replyWatchdogTimer: null,
      replyWatchdogDeadline: null,
      isUnmounted: false,
      lastMessageFromTutor: false,
      // Progress bar state
      currentDuration: 0,
      maxDuration: 150, // Freestyle default; lessons override with capSeconds
      unsubscribeDuration: null,
      unsubscribeCapSeconds: null,
      unsubscribeMessages: null,
      unsubscribeMessageChanges: null,
      unsubscribeChatComplete: null,
      sparkles: [],
      sparkleInterval: null,
      // Guards the completion haptic so it fires once per dialog activation,
      // not on every re-render or repeated onValue callback for the same
      // already-complete state. Also pre-set (without firing) by
      // watchForChatComplete when a chat is already complete on its initial
      // load, so opening a completed chat from history never buzzes; only a
      // live false->true transition while the chat is open should. Reset
      // alongside chatComplete in resetChatLog.
      completionHapticFired: false,
      keyboardVisible: false,
      viewportHandler: null,
      // Lesson active-engagement heartbeat. Accumulates foreground+visible time
      // locally and flushes it to dialog-meta/{id}/activeSeconds (the lesson's
      // active-time meter, distinct from the freestyle audio durationSeconds).
      pendingActiveSeconds: 0,
      activeTickInterval: null,
      pageVisible: true,
      appForeground: true,
      visibilityHandler: null,
      appStateListener: null,
    };
  },
  watch: {
    dialogId(newVal, oldVal) {
      this.resetChatLog();
      this.newChatSetup();
    },
    chatComplete(newVal) {
      if (newVal) {
        if (!this.completionHapticFired) {
          this.completionHapticFired = true;
          useHaptics().notifySuccess();
        }
        this.startSparkles();
        // Lesson is over; credit any remaining unflushed active time.
        this.flushActiveSeconds();
      } else {
        this.stopSparkles();
      }
    },
  },
  computed: {
    progressPercentage() {
      const type = this.dialogMeta?.type;
      if (type !== "FREESTYLE_CHAT" && type !== "LESSON") {
        return 0;
      }
      if (!this.maxDuration) {
        return 0;
      }

      const rawPercentage = (this.currentDuration / this.maxDuration) * 100;

      // Cap at 99% if duration exceeds max but chat isn't completed yet. For a
      // lesson this is the "wrapping up on your next reply" state: the cap is
      // reached but completedAt is only written on the next turn.
      if (rawPercentage >= 100 && !this.chatComplete) {
        return 99;
      }

      // Allow 100% only when chat is actually completed
      return Math.min(rawPercentage, 100);
    },
    progressBarLabel() {
      return this.dialogMeta?.type === "LESSON"
        ? "Lesson progress"
        : "Conversation progress";
    },
    // Drives the progress bar's fill color via a CSS class (defined in the
    // scoped <style> below) instead of an inline backgroundColor, so the
    // colors stay in --al-* tokens rather than JS-owned hex. The "complete"
    // class (DECISION D-A: green on completion) is applied separately in the
    // template and takes precedence over these thresholds in CSS.
    progressBarClass() {
      const progress = this.progressPercentage;
      const THRESHOLDS = { MEDIUM: 70, HIGH: 85 };

      if (this.chatComplete || progress === 100) {
        return "";
      }

      if (progress < THRESHOLDS.MEDIUM) return "is-low";
      if (progress < THRESHOLDS.HIGH) return "is-mid";
      return "is-high";
    },
  },
  methods: {
    async newChatSetup() {
      // Capture the dialog we're setting up. If the user switches chats (or the
      // component unmounts) while an await below is in flight, this.dialogId
      // changes; we must bail so we don't register listeners and a heartbeat
      // interval for a stale dialog (which would leak and mix chats).
      const setupDialogId = this.dialogId;

      // Load dialog metadata first
      const db = getDatabase();
      const dialogMetaRef = ref(db, `dialog-meta/${setupDialogId}`);
      const metaSnapshot = await get(dialogMetaRef);
      // isUnmounted as well as the dialog check: Vue leaves props intact on a
      // destroyed instance, so after an unmount this.dialogId still equals
      // setupDialogId and the guard alone would wave the stale setup through,
      // re-binding listeners and a heartbeat that nothing will ever tear down.
      if (this.isUnmounted || this.dialogId !== setupDialogId) return;
      if (metaSnapshot.exists()) {
        this.dialogMeta = metaSnapshot.val();
      }

      const existingMessageIds = await this.renderExistingMessagesAndGetIds(
        setupDialogId,
        this.dialogMeta?.clientStartedAt
      );
      // isUnmounted as well as the dialog check: Vue leaves props intact on a
      // destroyed instance, so after an unmount this.dialogId still equals
      // setupDialogId and the guard alone would wave the stale setup through,
      // re-binding listeners and a heartbeat that nothing will ever tear down.
      if (this.isUnmounted || this.dialogId !== setupDialogId) return;
      const lastMessageKey =
        existingMessageIds[existingMessageIds.length - 1] || null;

      // A send may be in flight from before a navigation away: the student's
      // message isn't persisted until the backend finishes transcription, so
      // the last persisted message can still be the previous tutor turn. Keep
      // the mic disabled until the tutor actually replies. We know it has
      // replied if a *new* tutor message (a different key than the one we
      // replied to) has arrived. See store/pendingTutorResponses.js.
      const pending = pendingTutorResponses.get(this.dialogId);
      const tutorHasReplied =
        pending &&
        this.mostRecentMessageIsFromTutor &&
        lastMessageKey !== pending.repliedToKey;

      if (pending && !tutorHasReplied) {
        // Still awaiting the tutor; keep the mic disabled. Pick the phase from
        // what's persisted: if the last message is the prior tutor turn, the
        // student's transcript hasn't landed yet (still transcribing) ->
        // "listening"; if it's the student's, the transcript is in and we're
        // waiting on the reply -> "thinking".
        const phase = this.mostRecentMessageIsFromTutor ? "listening" : "thinking";
        // Resume the turn's original deadline. The pending marker survives
        // navigation, so without this every remount handed the turn a fresh
        // 120s and reopening the chat could defer recovery indefinitely.
        if (pending.sentAt) {
          this.replyWatchdogDeadline = pending.sentAt + REPLY_WATCHDOG_MS;
        }
        this.appendLoadingMessage("tutor", phase);
      } else if (existingMessageIds.length === 0) {
        // Brand new chat — the tutor is generating the opening message. Mark
        // it awaiting so this turn gets the same remount-proof deadline as a
        // student send; otherwise reopening a stuck new chat kept handing it a
        // fresh 120s and the spinner never resolved.
        pendingTutorResponses.markAwaiting(setupDialogId, lastMessageKey);
        this.appendLoadingMessage("tutor");
      } else if (this.mostRecentMessageIsFromTutor) {
        // It's the student's turn.
        pendingTutorResponses.clear(this.dialogId);
        this.disableRecordButton = false;
      } else {
        // Last persisted message is the student's (e.g. an interrupted send
        // from another session) but the tutor hasn't replied yet — keep
        // waiting rather than re-enabling the mic. We cannot know when that
        // send happened, so anchor the deadline from now; the marker makes it
        // survive any further remount.
        pendingTutorResponses.markAwaiting(setupDialogId, lastMessageKey);
        this.appendLoadingMessage("tutor");
      }
      this.watchForNewMessages(existingMessageIds);
      this.watchForChatComplete();
      this.watchForDurationUpdate();
      this.startActiveTimeHeartbeat();
    },
    newRecordedAudio(payload) {
      this.disableRecordButton = true;
      // The reply-lag anchor, and the ONLY place one is created. It holds the
      // tap time in device-clock milliseconds so the reply this send earns can
      // report how long the learner actually waited for the text and for the
      // audio. Interaction submits, new-chat setup and the on-remount recovery
      // deliberately do not arm one: none of them is a send tap, so a lag
      // measured from them would be a wait nobody waited.
      const armedTapAtMs = replyLagAnchors.startTurn(
        this.dialogId,
        payload && payload.tapAtMs
      );
      // Remember we're awaiting a reply (keyed to the tutor turn we're replying
      // to) so navigating away and back doesn't re-enable the mic before the
      // tutor responds. See store/pendingTutorResponses.js.
      pendingTutorResponses.markAwaiting(
        this.dialogId,
        this.lastPersistedMessageKey()
      );
      // Tutor-side "Arti is listening..." while the recording is transcribed.
      // It flips to "Arti is thinking..." once the student's transcript lands
      // (a real backend signal), see watchForNewMessages.
      this.appendLoadingMessage("tutor", "listening");
      // The armed tap time travels with the send so its failure handler can
      // cancel THIS send's anchor and not a later one's.
      this.sendMessage(payload, armedTapAtMs);
    },
    lastPersistedMessageKey() {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].messageKey) return this.messages[i].messageKey;
      }
      return null;
    },
    async renderExistingMessagesAndGetIds(
      targetDialogId = this.dialogId,
      clientStartedAt = null
    ) {
      var existingMessageIds = [];
      const messagesRef = ref(getDatabase(), "messages/" + targetDialogId);
      await get(query(messagesRef, orderByChild("createdAt"))).then(
        (snapshot) => {
          // Bail if the dialog switched mid-fetch so we don't append a stale
          // chat's messages into the new one.
          if (this.dialogId !== targetDialogId) return;
          if (snapshot.exists()) {
            const fetched = [];
            snapshot.forEach((message) => {
              var messageData = message.val();
              messageData["autoPlay"] = false;
              messageData["messageKey"] = message.key;
              fetched.push(messageData);
            });
            // Every message this fetch finds is normally appended muted,
            // because only a message that arrives later through onChildAdded
            // is new to the learner and worth playing on its own. That holds
            // while the opening tutor message takes about thirteen seconds to
            // generate, since the fetch then always runs first and always
            // finds an empty node.
            //
            // A pre-authored opener is written a second or two after the
            // "start chat" tap, so on a slow device or against a fast function
            // the message can already be here when the fetch runs, and the
            // learner gets a finished opener sitting silently behind a play
            // button. Nothing in this component can settle which side of that
            // race wins, so restore autoplay for exactly the state it
            // produces: one tutor message, no student message, created within
            // a minute of the tap. A learner resuming an unanswered chat
            // inside that same minute also gets autoplay, which is acceptable.
            //
            // The flag has to be set before appendChatLog runs: AudioPlayer
            // copies its autoPlay prop into local state when it is created, so
            // mutating the message object afterwards would never reach it.
            //
            // Two conditions have to hold together, and between them they use
            // one clock and no clock at all.
            //
            // The tap that opened this chat must be less than a minute away
            // from now. clientStartedAt is written once, as
            // Math.floor(Date.now() / 1000), by whichever client created the
            // dialog, so comparing it with Date.now() here is a device-clock
            // reading against a device-clock reading and needs no tolerance
            // for skew between machines.
            //
            // The distance is absolute rather than one-sided, and that matters
            // in both directions. A tap can legitimately read a few seconds in
            // the future, because the clock that wrote it may be running
            // slightly fast or may belong to another of the learner's devices,
            // and rejecting every future-looking tap would silence the autoplay
            // this rule exists to restore. But leaving the past side unbounded
            // would mean a clock corrected backwards, or a dialog created on a
            // device running well ahead, made this clause true permanently
            // rather than for a minute. That is the same defect this rule
            // already had once, in a different form. Bounding both sides keeps
            // the small skew working and puts a real minute around the large
            // one. It also rejects a clientStartedAt written in milliseconds,
            // which dialog-meta has no .validate rule to prevent.
            //
            // And the message must not have been displayed before. seenAt is
            // stamped write-once by TutorMessage the first time the message
            // scrolls into view, so its absence means no mount has recorded
            // showing this message to this learner, which is the state the race
            // produces and the state a resume is not in. It uses no clock at
            // all, so it still bounds a replay when a device's clock is wrong
            // inside the tolerance above. It is evidence rather than proof:
            // that stamp is an RTDB write whose failure TutorMessage catches
            // and logs, so a learner who force-quits within a second of the
            // opener appearing can leave without it landing, and would hear the
            // opener once more on a reopen inside the same minute.
            //
            // Deliberately absent: any comparison against the message's own
            // createdAt. That field is stamped by the SERVER with
            // int(time.time()), and requiring a server timestamp and a device
            // timestamp to agree within a minute is the mistake this
            // repository has already had to correct once. The perceived
            // latency code was rebuilt around a device observed running 200
            // seconds fast, and the rule it settled on is written into
            // PerceivedLatencyTest.testFastDeviceClockDoesNotDropAPromptSample:
            // "The wait is same-clock, so it bounds the lag." A cross-clock
            // check here would silently stop autoplaying for exactly those
            // learners. It would also buy nothing, because a dialog whose tap
            // was under a minute ago is under a minute old, so its only
            // message is its opener whatever createdAt happens to say.
            const nowSeconds = Math.floor(Date.now() / 1000);
            const only = fetched.length === 1 ? fetched[0] : null;
            if (
              only &&
              only.speakerId === "tutor" &&
              !only.seenAt &&
              typeof clientStartedAt === "number" &&
              Math.abs(nowSeconds - clientStartedAt) <=
                FRESH_OPENER_WINDOW_SECONDS
            ) {
              only["autoPlay"] = true;
            }
            fetched.forEach((messageData) => {
              this.appendChatLog(messageData);
              existingMessageIds.push(messageData.messageKey);
            });
          }
        }
      );
      return existingMessageIds;
    },
    watchForNewMessages(existingMessageIds) {
      const db = getDatabase();
      const messagesRef = ref(db, "messages/" + this.dialogId);
      // onChildAdded is triggered once for each existing child and then again every time a new child is added.
      // Store the unsubscribe handle so resetChatLog/beforeUnmount can detach it —
      // a leaked listener bound to a stale dialogId would clear the wrong chat's
      // pending marker and append old messages to the new chat.
      this.unsubscribeMessages = onChildAdded(
        messagesRef,
        (snapshot) => {
          if (existingMessageIds.some((id) => id === snapshot.key)) {
            return;
          } else {
            this.removeLoadingMessage();
            // Read before this message is appended. The dialog's first tutor
            // message is its opener, which no send earned, so it is never
            // bound to a reply-lag anchor even when one is armed: that
            // happens when the opener stalled past the reply watchdog and the
            // learner spoke anyway, and binding it would record the opener as
            // that send's reply. Loading bubbles carry no messageKey and do
            // not count.
            const hasEarlierTutorMessage = this.messages.some(
              (message) => message && message.speaker === "tutor" && message.messageKey
            );
            var messageData = snapshot.val();
            messageData["autoPlay"] = messageData.speakerId === "tutor";
            messageData["messageKey"] = snapshot.key;
            this.appendChatLog(messageData);
            if (messageData.speakerId === "student") {
              // Transcript just landed — Arti is now composing the reply.
              // Anchor the turn if nothing did yet (a page reload mid-upload
              // loses the in-memory marker), but never overwrite an existing
              // one or the turn would silently get a fresh deadline.
              if (!pendingTutorResponses.get(this.dialogId)) {
                pendingTutorResponses.markAwaiting(
                  this.dialogId,
                  this.lastPersistedMessageKey()
                );
              }
              this.appendLoadingMessage("tutor", "thinking");
            } else {
              // The tutor replied: the turn is over. Drop the deadline even if
              // no bubble was showing to pop it.
              this.clearReplyWatchdog();
              this.disableRecordButton = false;
              pendingTutorResponses.clear(this.dialogId);
              // This message IS the reply to the send that armed the
              // anchor. Stamped inside nextTick because the field claims to
              // measure the text RENDERING: appendChatLog has only queued the
              // DOM update at this point, and this component already treats
              // nextTick as the moment the messages are painted (see
              // scrollToLatestMessage). No anchor — a reload mid-turn, an
              // opener, an interaction reply — writes nothing.
              const replyDialogId = this.dialogId;
              this.$nextTick(() => {
                // Re-checked after the tick: this.dialogId is mutable and the
                // learner can switch chats inside it, which would stamp this
                // message key under a different dialog and consume that
                // dialog's armed anchor.
                if (this.dialogId !== replyDialogId) {
                  // Release rather than leave armed: this listener's dialog is
                  // no longer on screen, so nothing here will bind that
                  // anchor, and an anchor left armed eventually attaches to
                  // an unrelated reply.
                  replyLagAnchors.cancelTurn(replyDialogId);
                  return;
                }
                // The opener: leave the anchor armed for the reply that follows.
                if (!hasEarlierTutorMessage) return;
                replyLagAnchors.bindToReply(replyDialogId, snapshot.key);
              });
            }
          }
        },
        (error) => {
          console.error("read failed:", error);
        }
      );
      // Text-first delivery: the tutor message lands without audio, then the
      // backend attaches audioId once synthesis finishes. Copy it onto the
      // rendered message; TutorMessage's v-if="audioId" mounts the player
      // fresh, and autoPlay makes it behave exactly like a legacy message.
      this.unsubscribeMessageChanges = onChildChanged(
        messagesRef,
        (snapshot) => {
          const changed = snapshot.val();
          if (!changed) return;
          const msg = this.messages.find((m) => m.messageKey === snapshot.key);
          if (!msg) return;
          // Before both early returns below: a tail arrives after the audio is
          // already attached, and a client that was offline can receive the
          // attach and the tail in one event. Only the replay object changes;
          // autoplay is still decided by attachFinalAudio alone.
          patchReplayFields(msg.replay, changed, { removeAbsent: true });
          // Segments come BEFORE the final attach, and they carry no audioId —
          // the guard here used to require one, which discarded every segment
          // write and left segmented delivery inert on the client.
          if (changed.segments) {
            this.handleSegmentsWrite(msg, changed.segments);
          }
          if (!changed.audioId) return; // e.g. seenAt updates
          if (msg.audioId) return; // only the audio-attach transition
          this.attachFinalAudio(msg, changed);
        },
        (error) => {
          console.error("message change watch failed:", error);
        }
      );
    },
    // A `segments` write on a tutor message that is still waiting for its
    // audio. The playback service owns every rule about what may start playing
    // and when; this hands it the ordered ids and a way to fetch the bytes.
    handleSegmentsWrite(msg, segments) {
      // Tutor replies only, and only before the stitched file lands. Neither
      // can happen today, but the autoplay path next to this one has always
      // checked the speaker, and a segments child on a student message would
      // otherwise play the learner's own recording back at them.
      if (msg.speaker !== "tutor" || msg.audioId) return;
      if (!msg.segmentSessionEligible) return;
      const audioIds = orderedSegmentAudioIds(segments);
      if (!audioIds.length) return;
      msg.segments = segments;
      msg.audioPending = true;
      offerSegments(
        { dialogId: this.dialogId, messageKey: msg.messageKey },
        audioIds,
        {
          fetchSegment: (audioId) => this.fetchSegmentAudio(audioId),
          renderedAt: msg.renderedAt,
          // A lesson or freestyle reply is heard at the speed chip's rate,
          // segment by segment, like its stitched file on the pill.
          followsSpeedChip: !!(msg.replay && msg.replay.playbackControls),
        }
      );
    },
    // The stitched whole file. Session existence — an in-memory fact this
    // client alone has — is what decides how it mounts.
    attachFinalAudio(msg, changed) {
      // The playback service decides, because only it knows how this
      // client's segment session for the message ended and whether the
      // learner actually heard any of it. For every message it holds a
      // session for it answers "mount paused", and when the reply failed
      // before the learner heard anything it plays the stitched file itself
      // (now, or later if the session is still live and fails then). It does
      // that rather than this component's autoplay because it re-checks the
      // decision after the bytes arrive: a record tap or another clip during
      // the fetch must still win. With no session at all it answers false,
      // which keeps today's behavior below.
      const mountPaused = completeSegmentSession(
        this.dialogId,
        msg.messageKey,
        changed.audioId
      );
      msg.audioId = changed.audioId;
      msg.audioPending = false;
      // The server deletes the segments children in the same update, so a
      // finished message is indistinguishable from a whole-path one.
      msg.segments = null;
      if (mountPaused) {
        // Whatever happened to the session, this component must not autoplay
        // the stitched file: the learner heard it, silenced it, moved on to
        // other audio, or is still hearing it, and in the one case where it
        // should play, the service plays it. The player mounts paused and
        // fetches only if they press play or scrub; while the service plays
        // the file, the player shows it as current through the shared state. The call above did NOT end a live session: the
        // stitch often finishes while the first sentence is still playing,
        // and stopping there would cut the reply off with autoplay already
        // disabled. The session keeps chaining the segments it was given and
        // ends itself when it runs out.
        msg.autoPlay = false;
        msg.deferAudioFetch = true;
        return;
      }
      // No session (a reload mid-turn, a second device, history): exactly
      // today's behavior.
      msg.autoPlay = changed.speakerId === "tutor";
    },
    // Bytes for one segment, on the same endpoint AudioPlayer uses for a whole
    // file. Always high priority: a segment is only ever fetched because the
    // tutor is about to speak it.
    async fetchSegmentAudio(audioId) {
      const params = new URLSearchParams({ audioId });
      const token = await authStore.getIdToken();
      const controller = new AbortController();
      const timer = setTimeout(
        () => controller.abort(),
        SEGMENT_FETCH_TIMEOUT_MS
      );
      try {
        const response = await fetch(
          cloudFunctions.getAudioApi() + "?" + params,
          {
            method: "GET",
            headers: { Authorization: "Bearer " + token },
            priority: "high",
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.blob();
      } finally {
        clearTimeout(timer);
      }
    },
    // What the message renders while its segments play: the speaking indicator
    // and the progressive text reveal are both scoped to messages this client
    // holds a session for, so neither touches history or a whole-file reply.
    segmentPhaseFor(msg) {
      if (!msg.messageKey) return null;
      return (
        segmentPlaybackState.phases[`${this.dialogId}/${msg.messageKey}`] || null
      );
    },
    resetChatLog() {
      // Stop the heartbeat before clearing dialog state. flush:false because
      // the dialogId prop has already advanced to the new chat.
      this.stopActiveTimeHeartbeat({ flush: false });
      this.messages = [];
      this.numMessages = 0;
      this.recordedAudio = null;
      this.disableRecordButton = true;
      this.chatComplete = false;
      this.completionHapticFired = false;
      this.dialogMeta = null;
      // Reset progress bar
      this.currentDuration = 0;
      this.maxDuration = 150;
      this.teardownChatListeners();
      // Stop sparkles
      this.stopSparkles();
    },
    teardownChatListeners() {
      // Segment sessions live in a module singleton, so leaving a chat has to
      // drop them explicitly or a stale one would claim a later message's
      // final attach and suppress its autoplay.
      endAllSegmentSessions();
      // The same for a phrase slice or its loop, which must not outlive the
      // dialog whose button stops it: FreeChatView and LessonsView tear the
      // chat down without a route change, so the router's pause never runs.
      // The clip cache belongs to the open dialog too.
      stopSlicesFor();
      clearClipCache();
      if (this.unsubscribeMessages) {
        this.unsubscribeMessages();
        this.unsubscribeMessages = null;
      }
      if (this.unsubscribeMessageChanges) {
        this.unsubscribeMessageChanges();
        this.unsubscribeMessageChanges = null;
      }
      if (this.unsubscribeChatComplete) {
        this.unsubscribeChatComplete();
        this.unsubscribeChatComplete = null;
      }
      if (this.unsubscribeDuration) {
        this.unsubscribeDuration();
        this.unsubscribeDuration = null;
      }
      if (this.unsubscribeCapSeconds) {
        this.unsubscribeCapSeconds();
        this.unsubscribeCapSeconds = null;
      }
      this.clearReplyWatchdog();
    },
    appendLoadingMessage(speaker, phase = "thinking") {
      this.messages.push({
        id: this.numMessages,
        speaker: speaker,
        isMessageLoading: true,
        phase: phase,
      });
      this.numMessages++;
      this.scrollToLatestMessage();
      this.startReplyWatchdog();
    },
    removeLoadingMessage() {
      // Guarded: only ever remove a loading bubble. Callers fire this
      // speculatively (every incoming child, completion, error paths) and an
      // unguarded pop deleted real messages (bug-hunt M2).
      const last = this.messages[this.messages.length - 1];
      if (!last || !last.isMessageLoading) return;
      this.messages.pop();
      this.numMessages--;
      // Only stop the timer. Removing the bubble does not end the turn: the
      // student's transcript landing swaps "listening" for "thinking", and
      // that turn keeps its original deadline (see startReplyWatchdog).
      this.pauseReplyWatchdog();
    },
    // The deadline belongs to the student's turn, not to any one bubble. It is
    // set on the first bubble of the turn and preserved across the
    // listening -> thinking swap, so a slow transcription cannot buy a second
    // full window and leave the learner waiting ~4 minutes.
    startReplyWatchdog() {
      this.pauseReplyWatchdog();
      // Never arm after the component is gone: newChatSetup is async and can
      // resume past beforeUnmount, and a stray timer would fire a toast onto
      // whatever screen the user has moved to.
      if (this.isUnmounted) return;
      if (!this.replyWatchdogDeadline) {
        this.replyWatchdogDeadline = Date.now() + REPLY_WATCHDOG_MS;
      }
      const remaining = Math.max(0, this.replyWatchdogDeadline - Date.now());
      this.replyWatchdogTimer = setTimeout(() => {
        this.replyWatchdogTimer = null;
        this.replyWatchdogDeadline = null;
        if (this.isUnmounted) return;
        // Nothing came back in time. This covers the whole send-to-reply round
        // trip, deliberately: the request may have died in the upload, or the
        // backend may have accepted it and then never produced a reply. Either
        // way the bubble would otherwise spin forever with the mic disabled
        // (bug-hunt L8), so recover rather than diagnose.
        this.removeLoadingMessage();
        this.clearReplyWatchdog();
        this.disableRecordButton = false;
        pendingTutorResponses.clear(this.dialogId);
        // The turn is over with no reply, so its lag anchor measures nothing.
        // The backend may still finish it, though, and a reply landing after
        // the learner records again must not be measured against that tap.
        replyLagAnchors.cancelTurn(this.dialogId, null, {
          replyMayStillArrive: true,
        });
        useToast().showToast(this.$t("errors.chat.replyTimeout"), "error");
      }, remaining);
    },
    // Stop the timer, keep the turn's deadline.
    pauseReplyWatchdog() {
      if (this.replyWatchdogTimer) {
        clearTimeout(this.replyWatchdogTimer);
        this.replyWatchdogTimer = null;
      }
    },
    // The turn is over (tutor replied, chat completed, send failed, teardown):
    // drop the deadline so the next turn starts a fresh window.
    clearReplyWatchdog() {
      this.pauseReplyWatchdog();
      this.replyWatchdogDeadline = null;
    },
    appendChatLog(messageData) {
      this.messages.push({
        id: this.numMessages,
        messageKey: messageData.messageKey,
        speaker: messageData.speakerId,
        text: messageData.text,
        spokenText: messageData.spokenText,
        audioId: messageData.audioId,
        // Text-first delivery leaves the reply audio-less until synthesis
        // finishes, and a segmented reply publishes its pieces under
        // `segments` while it waits. Both ride on the rendered model as the
        // client's record of what the server has published for this message.
        // Nothing renders from `audioPending` today - what the learner sees
        // while a reply is still arriving is driven by `segmentPhase`, which
        // travels separately - so treat it as state, not as a binding. A
        // session is started only from the child-changed path, never from
        // here: a reload mid-turn is deliberately a no-session case and gets
        // today's whole-file behavior when the stitched attach lands.
        audioPending: !!messageData.audioPending,
        segments: messageData.segments || null,
        // A message that was ALREADY carrying segments when it first rendered
        // is a reload mid-turn (or a second device): this client never heard
        // those segments and cannot know how much of the reply the learner
        // already heard elsewhere, so it must not start a session and replay
        // from the first sentence. The design calls that a no-session turn, and
        // it gets today's whole-file behavior when the stitched file attaches.
        // Only a message this client watched arrive audio-less is eligible.
        segmentSessionEligible: !messageData.segments,
        // Initialized alongside its siblings rather than appearing only when
        // the final attach assigns it, so the template binding never reads
        // undefined on a message's first render.
        deferAudioFetch: false,
        // When this message's text reached the screen. The playback service
        // compares it against the last "stop talking now" so a reply whose
        // audio arrives after the learner has already started recording does
        // not speak into that recording.
        renderedAt: Date.now(),
        // Carry the existing seenAt down so re-opening a chat never re-stamps
        // an already-seen tutor message; the field is write-once (see
        // TutorMessage.markMessageAsSeen) and feeds the perceived-latency metric.
        seenAt: messageData.seenAt,
        autoPlay: messageData.autoPlay,
        interaction: messageData.interaction || null,
        // Server-written on a pre-authored first tutor message only. It carries
        // the band, the response type, and the learner's native-language
        // explanation of the question, which TutorMessage renders as a
        // disclosure. Absent on every other message, so the whitelist normalizes
        // it to null the way it already does for interaction.
        opener: messageData.opener || null,
        // What the tutor's pill needs beyond the message itself, built once
        // here and patched in place by the change handler, never rebuilt in
        // the template: a heartbeat flush or a progress update must not hand
        // every tutor message a new object and re-render it.
        replay: buildReplay(messageData, this.dialogMeta),
      });
      this.mostRecentMessageIsFromTutor = messageData.speakerId === "tutor";
      this.numMessages++;
      this.scrollToLatestMessage();
    },
    // Arti's identity row (logo + name) appears once per run of consecutive
    // tutor messages; follow-ups in the run group visually beneath it.
    showTutorIdentity(index) {
      if (index === 0) return true;
      return this.messages[index - 1].speaker !== "tutor";
    },
    async scrollToLatestMessage() {
      await nextTick(); // required to make sure the messages are painted before scrolling https://vuejs.org/api/general.html#nexttick
      const messageContainer = this.$refs.messageContainer;
      if (messageContainer) {
        messageContainer.scrollTop = messageContainer.scrollHeight;
      }
    },
    isInteractionLive(msg, index) {
      // Live only while it is the latest message, unanswered, and the chat
      // is still open. Anything later (including a loading bubble) archives it.
      return (
        !!msg.interaction &&
        !msg.interaction.response &&
        !this.chatComplete &&
        index === this.messages.length - 1
      );
    },
    async handleInteractionSubmit(msg, payload) {
      const sendDialogId = this.dialogId;
      this.disableRecordButton = true;
      pendingTutorResponses.markAwaiting(sendDialogId, this.lastPersistedMessageKey());
      // Interaction turns are excluded from lag stamping on both sides, so any
      // anchor still armed from an earlier voice send must go rather than bind
      // to the reply this card is about to earn.
      replyLagAnchors.cancelTurn(sendDialogId);
      this.appendLoadingMessage("tutor", "thinking");
      try {
        const token = await authStore.getIdToken();
        const response = await fetch(cloudFunctions.getContinueChatApi(), {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            dialogId: sendDialogId,
            action: "interactionResponse",
            interactionResponse: { messageKey: msg.messageKey, ...payload },
          }),
        });
        if (!response.ok) {
          // Read the body BEFORE throwing: the bounded-turn refusals name
          // themselves in it, and the old `throw new Error(...)` discarded it.
          const { code } = await readRefusal(response);
          throw refusalError(response.status, code);
        }
      } catch (error) {
        console.error("Failed to submit interaction response:", error);
        // Always release the dialog this send belonged to, even if the user
        // has moved on, or it stays stuck awaiting a reply that failed.
        pendingTutorResponses.clear(sendDialogId);
        // Interaction replies are never measured, but one the backend still
        // writes after this failure could land after the learner's next voice
        // send has armed an anchor, and would bind it.
        replyLagAnchors.cancelTurn(sendDialogId, null, {
          replyMayStillArrive: replyMayStillArrive(error),
        });
        // But only touch the UI while it still belongs to that send: after an
        // unmount or a dialog switch this would pop another chat's bubble,
        // clear its watchdog and toast over whatever the user is now looking at.
        if (this.isUnmounted || this.dialogId !== sendDialogId) return;
        this.removeLoadingMessage();
        this.clearReplyWatchdog();
        this.disableRecordButton = false;
        useToast().showToast(
          this.$t(error?.refusalKey || "interactions.submitFailed"),
          "error"
        );
      }
    },
    async sendMessage(payload, armedTapAtMs = null) {
      // Accept either the new { blob, extension, mimeType } shape from
      // AudioRecorder or a raw Blob (in case any other caller still passes one).
      const sendDialogId = this.dialogId;
      const blob = payload && payload.blob ? payload.blob : payload;
      const extension =
        (payload && payload.extension) ||
        audioConfig.studentMessageFileName.split(".").pop() ||
        "webm";
      const fileName = `audio.${extension}`;

      const formData = new FormData();
      formData.append("studentAudio", blob, fileName);
      formData.append(
        "json",
        // sentAt (seconds): the moment the recording is sent — the true start of
        // the reply wait, before upload + transcription (~9s). The server writes
        // the student message's createdAt only after transcription, so
        // tutor-replyWait anchors on this. See recordPerceivedLatencyMetrics.
        // clientMessageId identifies this submission so a copy re-issued below
        // the application layer is suppressed rather than answered twice.
        // Minted once, here, so both copies of one send carry the same value.
        // audioSegments declares what this BUILD can do, not what this user is
        // entitled to: the server may only split a reply into segments for a
        // client that implements segment playback, and a runtime flag cannot
        // tell it which app build is installed. Old builds never send it and
        // keep today's whole-file delivery whatever the flag says.
        JSON.stringify({
          dialogId: sendDialogId,
          sentAt: Math.floor(Date.now() / 1000),
          clientMessageId: newClientMessageId(),
          audioSegments: true,
        })
      );

      try {
        // Inside the try: an expired session or a failed offline token refresh
        // rejects here, and outside it that rejection escaped every recovery
        // path, leaving the dialog marked as awaiting a reply that was never
        // sent.
        const token = await authStore.getIdToken();
        const response = await fetch(cloudFunctions.getContinueChatApi(), {
          method: "POST",
          headers: { Authorization: "Bearer " + token },
          body: formData,
        });
        if (!response.ok) {
          // Read the body BEFORE throwing. On this path the body is
          // best-effort: a limiter refusal is answered while the browser may
          // still be uploading the recording, so the response can arrive with
          // no readable body at all. `readRefusal` reports that as no code and
          // the catch below falls back to the existing toast.
          const { code } = await readRefusal(response);
          throw refusalError(response.status, code);
        }
        // Deliberately nothing else on success, including when the body says
        // `{"duplicate": true}`. A suppressed duplicate IS a success: the turn
        // it duplicated was already accepted, the RTDB onChildAdded listener
        // stays the source of truth for what is rendered, and the 120-second
        // reply watchdog stays the recovery net. Clearing the pending marker or
        // popping the loading bubble here would hand the mic back while a reply
        // is still being generated.
      } catch (error) {
        console.error("Failed to upload recording:", error);
        // Always release the dialog this send belonged to, even if the user
        // has moved on, or it stays stuck awaiting a reply that failed.
        pendingTutorResponses.clear(sendDialogId);
        // Same for the lag anchor: no reply is coming for this tap, and an
        // anchor left armed would attach itself to whatever reply arrives
        // next for this dialog. Identified by the tap time, because an upload
        // can outlive the watchdog and reject after a second send has already
        // armed an anchor of its own. Unless the failure was a refusal, the
        // backend may still write this send's reply, and the store must not
        // measure that late reply against the learner's next tap.
        replyLagAnchors.cancelTurn(sendDialogId, armedTapAtMs, {
          replyMayStillArrive: replyMayStillArrive(error),
        });
        // But only touch the UI while it still belongs to that send: after an
        // unmount or a dialog switch this would pop another chat's bubble,
        // clear its watchdog and toast over whatever the user is now looking at.
        if (this.isUnmounted || this.dialogId !== sendDialogId) return;
        this.removeLoadingMessage();
        this.clearReplyWatchdog();
        this.disableRecordButton = false;
        useToast().showToast(
          this.$t(error?.refusalKey || "audioRecorder.recordingFailed"),
          "error"
        );
      }
    },
    watchForChatComplete() {
      const db = getDatabase();
      const isCompleteRef = ref(
        db,
        "dialog-meta/" + this.dialogId + "/completedAt"
      );
      // onValue fires immediately with the current DB value, then again on
      // every future change. The first callback is that initial snapshot; if
      // it already shows the chat complete (opening a finished chat from
      // history, or switching back to one that finished earlier this
      // session), pre-set completionHapticFired instead of letting the
      // chatComplete watcher fire the haptic. Only a genuine false->true
      // transition seen after this point is a live completion.
      let initialSnapshotSeen = false;
      this.unsubscribeChatComplete = onValue(
        isCompleteRef,
        (snapshot) => {
          if (snapshot.val()) {
            if (!initialSnapshotSeen) {
              this.completionHapticFired = true;
            }
            this.chatComplete = true;
            // The chat is over; the mic is replaced by the completion UI.
            pendingTutorResponses.clear(this.dialogId);

            // Remove a stale loading bubble if one is showing; the guard
            // inside makes this a no-op when the last message is real. The
            // old else-branch popped the student's final bubble when a chat
            // completed on a student turn (bug-hunt M2).
            this.removeLoadingMessage();
            this.clearReplyWatchdog();
          }
          initialSnapshotSeen = true;
        },
        (error) => {
          console.error("read failed:", error);
        }
      );
    },
    watchForDurationUpdate() {
      const type = this.dialogMeta?.type;
      if (type !== "FREESTYLE_CHAT" && type !== "LESSON") return;

      const db = getDatabase();

      // Lessons fill toward the per-lesson active-time cap using the
      // activeSeconds meter; freestyle fills toward the fixed audio cap.
      const field = type === "LESSON" ? "activeSeconds" : "durationSeconds";
      if (type === "LESSON") {
        // capSeconds is written by startNewChat AFTER this screen first reads
        // dialog-meta, so a one-shot read misses it for a brand-new lesson and
        // the bar would wrongly use the 150s freestyle default. Watch it so
        // maxDuration self-corrects the moment it lands.
        const capRef = ref(db, `dialog-meta/${this.dialogId}/capSeconds`);
        this.unsubscribeCapSeconds = onValue(
          capRef,
          (snapshot) => {
            const cap = snapshot.val();
            if (cap) this.maxDuration = cap;
          },
          (error) => console.error("capSeconds watch failed:", error)
        );
      }

      const durationRef = ref(db, `dialog-meta/${this.dialogId}/${field}`);

      this.unsubscribeDuration = onValue(
        durationRef,
        (snapshot) => {
          this.currentDuration = snapshot.val() || 0;
        },
        (error) => console.error("Duration update failed:", error)
      );
    },
    // --- Lesson active-engagement heartbeat -------------------------------
    // Counts only foreground + visible + not-yet-complete time, so a learner
    // who leaves mid-lesson and returns hours later resumes where they left
    // off instead of being timed out. The accumulated seconds are flushed to
    // the server (an increment transaction, multi-tab safe); the server value
    // is what drives both the bar and the backend cap, so a few unflushed
    // seconds only affect the live bar, never completion correctness.
    startActiveTimeHeartbeat() {
      if (this.dialogMeta?.type !== "LESSON") return;

      this.pageVisible =
        typeof document === "undefined" ||
        document.visibilityState === "visible";
      this.appForeground = true;

      this.visibilityHandler = () => {
        this.pageVisible = document.visibilityState === "visible";
        if (!this.pageVisible) this.flushActiveSeconds();
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);

      this.initAppStateListener();

      this.activeTickInterval = setInterval(() => {
        if (!this.isLessonTimerRunning()) return;
        this.pendingActiveSeconds += 1;
        // Flush in small batches so the bar stays roughly live and a crash
        // loses at most ~20s of credited time.
        if (this.pendingActiveSeconds >= 20) this.flushActiveSeconds();
      }, 1000);
    },
    isLessonTimerRunning() {
      return (
        this.dialogMeta?.type === "LESSON" &&
        !this.chatComplete &&
        this.pageVisible &&
        this.appForeground
      );
    },
    async initAppStateListener() {
      // Native webviews don't reliably fire visibilitychange when the app is
      // backgrounded, so gate on Capacitor's app-state too. Web has no native
      // platform; visibilitychange covers it there.
      if (!window.Capacitor?.isNativePlatform?.()) return;
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const listener = await CapApp.addListener(
          "appStateChange",
          ({ isActive }) => {
            this.appForeground = isActive;
            if (!isActive) this.flushActiveSeconds();
          }
        );
        // These registrations are async; if the component was torn down while
        // awaiting (stopActiveTimeHeartbeat clears activeTickInterval), the
        // listener would otherwise leak and fire on a destroyed component.
        if (!this.activeTickInterval) {
          listener.remove();
          return;
        }
        this.appStateListener = listener;
      } catch (error) {
        console.error("Failed to init app-state listener:", error);
      }
    },
    async flushActiveSeconds() {
      const dialogId = this.dialogId;
      if (!this.pendingActiveSeconds || !dialogId || this.dialogMeta?.type !== "LESSON")
        return;

      // Cap each write below the .validate per-write delta limit (3600) so a
      // large backlog (e.g. a long offline-but-foreground stretch) can't produce
      // a write that's rejected forever. The remainder drains on later flushes.
      const seconds = Math.min(this.pendingActiveSeconds, 3500);
      // Deduct optimistically so the next tick doesn't double-count; re-add on
      // failure so the next flush retries.
      this.pendingActiveSeconds -= seconds;
      try {
        const db = getDatabase();
        const activeRef = ref(db, `dialog-meta/${dialogId}/activeSeconds`);
        await runTransaction(activeRef, (cur) => (cur || 0) + seconds);
      } catch (error) {
        console.error("Failed to flush active seconds:", error);
        this.pendingActiveSeconds += seconds;
      }
    },
    stopActiveTimeHeartbeat({ flush } = { flush: true }) {
      if (this.activeTickInterval) {
        clearInterval(this.activeTickInterval);
        this.activeTickInterval = null;
      }
      if (this.visibilityHandler) {
        document.removeEventListener("visibilitychange", this.visibilityHandler);
        this.visibilityHandler = null;
      }
      if (this.appStateListener) {
        this.appStateListener.remove();
        this.appStateListener = null;
      }
      if (flush) {
        this.flushActiveSeconds();
      } else {
        // Switching dialogs: the dialogId prop has already changed, so a flush
        // would credit the wrong lesson. Drop the small (<=20s) remainder; the
        // server backstop still enforces the cap.
        this.pendingActiveSeconds = 0;
      }
    },
    emitNewChatRequest() {
      this.$emit("newChatRequest");
    },
    startSparkles() {
      this.stopSparkles(); // Clear any existing interval
      let sparkleId = 0;

      // Create initial burst of 2 sparkles
      this.createSparkle(sparkleId++);
      setTimeout(() => this.createSparkle(sparkleId++), 150);

      // Add new sparkles periodically
      this.sparkleInterval = setInterval(() => {
        this.createSparkle(sparkleId++);

        // Keep only 4 sparkles max (reduced from 8)
        if (this.sparkles.length > 4) {
          this.sparkles.shift();
        }
      }, 500); // Faster than current 800ms
    },
    createSparkle(id) {
      // Pre-defined zones for better distribution
      const zones = [15, 35, 50, 65, 85];
      const zone = zones[Math.floor(Math.random() * zones.length)];
      const offset = (Math.random() - 0.5) * 10; // ±5% variation

      // Size variety: 40% small, 40% medium, 20% large
      const sizes = ["small", "medium", "large"];
      const sizeWeights = [0.4, 0.4, 0.2];
      const rand = Math.random();
      let size;
      if (rand < sizeWeights[0]) size = sizes[0];
      else if (rand < sizeWeights[0] + sizeWeights[1]) size = sizes[1];
      else size = sizes[2];

      const sparkle = {
        id: id,
        position: zone + offset,
        size: size,
        delay: Math.random() * 0.2, // 0-200ms stagger
      };
      this.sparkles.push(sparkle);
    },
    stopSparkles() {
      if (this.sparkleInterval) {
        clearInterval(this.sparkleInterval);
        this.sparkleInterval = null;
      }
      this.sparkles = [];
    },
    initKeyboardHandler() {
      if (!window.visualViewport) return;

      const initialHeight = window.visualViewport.height;
      this.viewportHandler = () => {
        const currentHeight = window.visualViewport.height;
        const diff = initialHeight - currentHeight;
        const wasVisible = this.keyboardVisible;
        this.keyboardVisible = diff > 100;

        if (this.keyboardVisible && !wasVisible) {
          // Keyboard just opened — scroll to bottom
          this.scrollToLatestMessage();
        }
      };

      window.visualViewport.addEventListener("resize", this.viewportHandler);
    },
  },
  async mounted() {
    this.userId = authStore.getUserId();

    this.newChatSetup();
    this.initKeyboardHandler();
  },
  beforeUnmount() {
    this.isUnmounted = true;
    this.stopActiveTimeHeartbeat({ flush: true });
    this.teardownChatListeners();
    this.stopSparkles();
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.viewportHandler);
    }
  },
};
</script>

<style scoped>
.chat-container,
.chat-row {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

@media (max-width: 767px) {
  .chat-container,
  .chat-row {
    /* Full height: the card runs to the true screen bottom and the footer's
       padding reserves the tab bar + safe area. Subtracting the safe-area
       inset here as well double-counts it and floats the mic button. */
    min-height: 100%;
    height: 100%;
  }
}

.chat {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: visible;
  width: 100%;
}

.card {
  height: 100%;
  width: 100%;
  background-color: var(--al-surface-raised) !important;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--al-shadow-sm);
  border: 1px solid var(--al-border);
}

@media (max-width: 767px) {
  .card {
    min-height: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    border: none;
    margin: 0;
    padding: 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
  }
}

.msg_head {
  position: relative;
  background-color: var(--al-surface-raised);
  padding-top: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--al-border);
}

.msg_card_body {
  overflow-y: auto;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  padding-bottom: 10px;
  /* DESIGN.md screen padding: nothing in the conversation (bubbles, identity
     row, lesson content cards) may touch the screen edge. .card-body carries
     no padding of its own (the Bootstrap class is vestigial), so this is the
     single source of horizontal inset for the whole scroll area.
     max() with the side safe-area envs: landscape is enabled on iPhone and
     the notch's lateral inset (44-59px) exceeds 16px there; portrait envs
     are 0 so the 16px base always wins upright. */
  padding-left: max(16px, env(safe-area-inset-left, 0px));
  padding-right: max(16px, env(safe-area-inset-right, 0px));
  /* Page tint gives Arti's open-canvas messages and their raised content
     cards (tables, audio pills) a surface to sit on. */
  background-color: var(--al-surface-page);
}

.messages-wrapper {
  flex: 1 1 auto;
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

@media (max-width: 767px) {
  .msg_card_body {
    flex: 1 1 auto;
    padding-bottom: 20px;
    /* Ensures container shrinks when keyboard is open */
    min-height: 0;
    height: auto;
    max-height: calc(100% - 160px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.card-header {
  border-bottom: 1px solid var(--al-border) !important;
  margin: 0;
  padding: 15px 20px;
  z-index: 48;
  background-color: var(--al-surface-raised);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Never shrink: header/footer are flex items of .card, and an explicit
     min-height disables the automatic content-size floor, so a long message
     list would otherwise squeeze the header and push the logo up under the
     status bar. */
  flex-shrink: 0;
}

.card-footer {
  border-top: 0 !important;
  text-align: center;
  padding: 12px;
  background-color: var(--al-surface-raised);
  position: relative;
  z-index: 10;
  flex-shrink: 0;
}

/* The mobile overrides below use `.card .card-*` (higher specificity) rather
   than relying on source order: the production CSS minifier reorders
   equal-specificity duplicate selectors, which silently disabled these rules
   in release builds while dev builds looked fine. */
@media (max-width: 767px) {
  .card .card-header {
    /* 16px sides match .msg_card_body so header content and the conversation
       share one screen-edge inset (same max() cutout guard for landscape). */
    padding: 12px 16px 12px 16px;
    padding-left: max(16px, env(safe-area-inset-left, 0px));
    padding-right: max(16px, env(safe-area-inset-right, 0px));
    padding-top: var(--safe-top, env(safe-area-inset-top, 16px));
    min-height: calc(56px + var(--safe-top, env(safe-area-inset-top, 0px)));
    position: sticky;
    top: 0;
  }

  .user_img {
    height: 40px;
    width: 40px;
  }

  .img_cont {
    height: 40px;
    width: 40px;
  }

  .user_info span {
    font-size: 16px;
  }

  .d-flex.bd-highlight {
    width: 100%;
    justify-content: center;
  }

  .card .card-footer {
    /* The card bottom sits at the screen bottom, so this padding clears the
       fixed BottomTabBar (56px + --al-tab-bar-inset) and leaves exactly 12px
       of visible space below the mic, matching the 12px top padding. */
    padding: 12px 16px calc(56px + var(--al-tab-bar-inset) + 12px);
    padding-left: max(16px, env(safe-area-inset-left, 0px));
    padding-right: max(16px, env(safe-area-inset-right, 0px));
    position: sticky;
    bottom: 0;
    background-color: var(--al-surface-raised);
    z-index: 10;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    margin-top: auto;
  }

  /* Let the recorder span the footer on mobile so its recording-state pill
     (width: 100% in AudioRecorder) stretches edge to edge; without this the
     anonymous flex item collapses to content width and the 100% is a no-op.
     Desktop keeps the centered content-width pill. */
  .card .card-footer .recorder-slot {
    flex: 1;
    min-width: 0;
  }

  /* Same reason as the recorder above: the completion footer is the other
     thing that can occupy this row, and LessonComplete's trigger is
     width: 100% on phones, which is a no-op while this wrapper is shrink to
     fit. justify-content: center is what keeps the other three completion
     components (ChatFinishedStats, AssessmentComplete,
     BackgroundCollectionComplete) looking exactly as they do today: their
     roots are content-width, so they would slide from centred to left-aligned
     once the wrapper grows. */
  .card .card-footer .view-stats {
    flex: 1;
    min-width: 0;
    justify-content: center;
  }
}

.view-stats {
  display: flex;
}

.container {
  align-content: center;
}

.user_img {
  height: 63px;
  width: 63px;
  border: 1.5px solid var(--al-border);
  object-fit: contain;
}

.img_cont {
  position: relative;
  height: 63px;
  width: 63px;
  margin-right: 0.5rem;
}

.online_icon {
  position: absolute;
  height: 15px;
  width: 15px;
  background-color: var(--al-success);
  border-radius: 50%;
  bottom: 0.2em;
  right: 0.4em;
  border: 1.5px solid var(--al-surface-raised);
}

.user_info {
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 15px;
}

.user_info span {
  font-size: 20px;
  color: var(--al-text);
  font-weight: bold;
}

.user_info p {
  font-size: 10px;
  color: var(--al-text-muted);
}

/* width */
::-webkit-scrollbar {
  width: 10px;
}

/* Track */
::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px var(--al-border);
  border-radius: 10px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: var(--al-border);
  border-radius: 10px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: var(--al-text-muted);
}

/* Specific adjustments for Android devices (like Pixel) */
@media (max-width: 767px) and (min-resolution: 2.5dppx) {
  .msg_card_body {
    max-height: calc(100% - 140px);
  }
}

/* Progress bar for freestyle chats */
.progress-bar-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background-color: var(--al-surface-sunken);
  overflow: visible; /* Allow sparkles to overflow */
}

.progress-bar-fill {
  height: 100%;
  transition: width var(--al-dur-medium) var(--al-ease-move),
    background-color var(--al-dur-short) var(--al-ease-move);
  will-change: width;
}

/* Honest JS-driven mirror of --al-* tokens: progressBarClass() (ChatScreen.vue)
   picks one of these classes instead of writing an inline backgroundColor, so
   the fill color still comes from the token system rather than JS-owned hex. */
.progress-bar-fill.is-low {
  background-color: var(--al-light-blue);
}

.progress-bar-fill.is-mid {
  background-color: var(--al-primary);
}

.progress-bar-fill.is-high {
  background-color: var(--al-primary-hover);
}

/* DECISION D-A: completion is green, not the legacy orange. */
.progress-bar-fill.complete {
  background-color: var(--al-success);
}

/* Modern CSS star sparkles */
@keyframes sparkle-shine {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0) rotate(0deg);
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
    transform: translate(-50%, -6px) scale(1) rotate(15deg);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -8px) scale(0.5) rotate(15deg);
  }
}

.progress-bar-fill.complete {
  position: relative;
  overflow: visible;
}

.sparkle {
  position: absolute;
  top: -2px;
  pointer-events: none;
  animation: sparkle-shine 0.8s ease-out forwards;
  /* DECISION D-A: sparkles are green/white to match the green completion bar. */
  filter: drop-shadow(0 0 2px rgba(16, 185, 129, 0.6));
}

/* CSS star shape using clip-path */
.sparkle::before {
  content: "";
  display: block;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9),
    rgba(16, 185, 129, 0.8)
  );
  clip-path: polygon(
    50% 0%,
    61% 35%,
    98% 35%,
    68% 57%,
    79% 91%,
    50% 70%,
    21% 91%,
    32% 57%,
    2% 35%,
    39% 35%
  );
}

/* Size variants */
.sparkle--small::before {
  width: 4px;
  height: 4px;
}

.sparkle--medium::before {
  width: 6px;
  height: 6px;
}

.sparkle--large::before {
  width: 8px;
  height: 8px;
}

/* Sparkles are decorative celebration, not information; drop them entirely
   under reduced motion rather than showing a static star. The green fill on
   .complete still communicates completion on its own. */
@media (prefers-reduced-motion: reduce) {
  .progress-bar-fill {
    transition: none;
  }

  .sparkle {
    display: none;
  }
}
</style>
